import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { fetchObjectivesA } from '~src/store/okrs/actions';
import { ObjectiveFilterTypes, ObjectivesQueryI } from '~src/store/okrs/interfaces';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { ObjectiveI, ObjectivesResponse } from '~src/store/okrs/keyResultStream/interfaces';
import {
  CycleHeader,
  RefreshWrapper,
  SpinnerContainer,
  StyledSideBarLink,
  SubNavItemLinkText,
  SubTitle,
} from '~src/shared/sidebar/styles/subnav.sc';
import { StoreI } from '~src/rootReducer';
import { mapIdToData } from '~src/utils';
import { okrApi } from '~src/services/ApiController';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import SvgIcon from '~src/shared/SvgIcon';
import { routes } from '~src/routingConfig';
import { translateContent } from '~src/services/translationServices';

interface StateProps {
  objectives: { [key: string]: ObjectiveI };
}

interface DispatchProps {
  fetchObjectives: (query?: ObjectivesQueryI) => ApiReduxResp<ObjectivesResponse>;
}

type Props = StateProps & DispatchProps;

const KeyResultSubNav: FC<Props> = () => {
  const [objectives, setObjectives] = useState<ObjectiveI[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchObjectives();
  }, []);

  const fetchObjectives = () => {
    setLoading(true);
    // cannot use the redux store because it would mix up the result with the objective list
    okrApi
      .get(`objectives?${ObjectiveFilterTypes.Stream}=true`)
      .then(res => {
        setObjectives(res.data.items);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const [t] = useTranslation();

  const refreshClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    fetchObjectives();
  };

  return (
    <div>
      <StyledSideBarLink to={routes.okrs.objectivesStream.updateObjectiveKRs}>
        <CycleHeader>
          <span data-translation="x.sidebar.subnav.key_result.title">
            {t('x.sidebar.subnav.key_result.title')}
          </span>
          <RefreshWrapper onClick={refreshClick}>
            <SvgIcon name="iconRefresh" width="20px" />
          </RefreshWrapper>
        </CycleHeader>
      </StyledSideBarLink>
      {loading ? (
        <SpinnerContainer>
          <LoadingSpinner />
        </SpinnerContainer>
      ) : (
        ((objectives && objectives.length > 0) || loading) && (
          <>
            <SubNavItemLinkText data-translation="x.sidebar.subnav.key_result.sub_title" className="overline">
              <SubTitle>{t('x.sidebar.subnav.key_result.sub_title')}</SubTitle>
            </SubNavItemLinkText>
            {objectives.map(objective => (
              <StyledSideBarLink
                key={objective.id}
                to={`${routes.okrs.objectivesStream.updateObjectiveKRs}/${objective.id}`}
              >
                <SubNavItemLinkText>{translateContent(objective.name)}</SubNavItemLinkText>
              </StyledSideBarLink>
            ))}
          </>
        )
      )}
    </div>
  );
};

const mapStateToProps = (state: StoreI): StateProps => ({
  objectives: state.okrs.objectives.objectivesListData
    ? mapIdToData(state.okrs.objectives.objectivesListData.items)
    : {},
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchObjectives: query => dispatch(fetchObjectivesA(query)),
});

const KeyResultSubNavConnected = connect(mapStateToProps, mapDispatchToProps)(KeyResultSubNav);
export default KeyResultSubNavConnected;
